import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { dispatcherFactory, selectorHookFactory } from '~/store/utils'

/** Mutually exclusive table filter selected via Health tile metrics. `null` = no filter. */
export type BatteryTableFilter = 'alarms' | 'offline' | 'lowSoc' | null

type BatteryOverviewSlice = {
  orgIds: string[]
  siteIds: string[]
  tableFilter: BatteryTableFilter
}

const initialState: BatteryOverviewSlice = {
  orgIds: [],
  siteIds: [],
  tableFilter: null
}

const batteryOverviewSlice = createSlice({
  name: 'batteryOverview',
  initialState,
  reducers: {
    reset: () => initialState,
    setOrgIds: (state, action: PayloadAction<string[]>) => ({ ...state, orgIds: action.payload }),
    setSiteIds: (state, action: PayloadAction<string[]>) => ({ ...state, siteIds: action.payload }),
    setTableFilter: (state, action: PayloadAction<BatteryTableFilter>) => ({
      ...state,
      tableFilter: action.payload
    })
  }
})

export default batteryOverviewSlice.reducer

export const useBatteryOverviewOrgIds = selectorHookFactory((state) => state.batteryOverview.orgIds)
export const useBatteryOverviewSiteIds = selectorHookFactory((state) => state.batteryOverview.siteIds)
export const useBatteryOverviewTableFilter = selectorHookFactory((state) => state.batteryOverview.tableFilter)
export const useBatteryOverviewFilterCount = selectorHookFactory(
  (state) => (state.batteryOverview.orgIds.length > 0 ? 1 : 0) + (state.batteryOverview.siteIds.length > 0 ? 1 : 0)
)

const { reset, setOrgIds, setSiteIds, setTableFilter } = batteryOverviewSlice.actions
export { reset, setTableFilter }

export const useBatteryOverviewUpdateOrgIds = dispatcherFactory(setOrgIds)
export const useBatteryOverviewUpdateSiteIds = dispatcherFactory(setSiteIds)
export const useBatteryOverviewSetTableFilter = dispatcherFactory(setTableFilter)

/** Selected org IDs from filters, or the current org when none are selected */
export function useBatteryOverviewOrgIdsOrCurrent() {
  const orgIds = useBatteryOverviewOrgIds()
  const orgId = useCurrentOrgId()
  return orgIds.length > 0 ? orgIds : [orgId]
}
